import cx from 'classnames';
import {type FC, memo, useCallback, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector} from 'react-redux';
import type {UnknownAction} from 'redux';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {useFeatureControls} from '~/CreateFabricInvitation/hooks/useFeatureControls';
import {selectFabricDesign} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {setIsEditingDesign} from '~/CreateFabricInvitation/reducers/preview/reducer';
import {eventIsPremium} from '~/common/fabric/reducers/details/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectUsedPremiumFeature} from '~/common/fabric/reducers/fabricDesign/selectors';
import {shouldUseNamedStepsNav} from '~/common/utils/event_utils';
import {initPremiumDesign} from '~/ViewInvitation/reducers/premiumDesign';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {XIcon} from '~library/svg/v2/XIcon';

import './PreviewDesignEditorNav.sass';

interface PreviewDesignEditorNavContentProps {
  // biome-ignore lint/suspicious/noExplicitAny: fabricDesign store not yet converted to TypeScript
  savedDesign: Record<string, any>;
}

const PreviewDesignEditorNavContent: FC<PreviewDesignEditorNavContentProps> = ({savedDesign}) => {
  const dispatch = useDispatch();
  const isDesktop = useIsLargishAndUp();
  const {unlockDesignTools} = useFeatureControls();
  const useNamedStepsNav = shouldUseNamedStepsNav();
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const isPremium: boolean = useSelector(eventIsPremium);
  const usedPremiumFeature: boolean = useSelector(selectUsedPremiumFeature);

  const applyChanges = useCallback(() => {
    dispatch(saveEventThunk(false) as unknown as UnknownAction);
    if (window.evite_event) {
      dispatch(initPremiumDesign({premiumDesign: window.evite_event.options.design}));
    }
    dispatch(setIsEditingDesign(false));
  }, [dispatch]);

  const handleSave = useCallback(() => {
    if (!isPremium && usedPremiumFeature) {
      unlockDesignTools();
    } else {
      applyChanges();
    }
  }, [isPremium, usedPremiumFeature, unlockDesignTools, applyChanges]);

  const confirmCancel = useCallback(() => {
    dispatch({type: UPDATE_DESIGN_OPTIONS, payload: savedDesign});
    if (window.evite_event) {
      dispatch(initPremiumDesign({premiumDesign: window.evite_event.options.design}));
    }
    dispatch(saveEventThunk(false) as unknown as UnknownAction);
    dispatch(setIsEditingDesign(false));
  }, [dispatch, savedDesign]);

  const handleCancel = useCallback(() => {
    setLeaveModalOpen(true);
  }, []);

  const closeLeaveModal = useCallback(() => setLeaveModalOpen(false), []);

  return (
    <div
      className={cx('preview-design-editor-nav', {
        'preview-design-editor-nav--cf-nav-variant': useNamedStepsNav && !isDesktop,
      })}
    >
      <Button
        variant={isDesktop ? 'unstyled-button' : 'secondary'}
        data-qa-id="cancel-edit-design"
        size={isDesktop ? 'small' : 'large'}
        onClick={handleCancel}
      >
        {isDesktop ? <XIcon ratio={0.75} /> : 'Cancel'}
      </Button>
      {isDesktop && (
        <Typography
          variant="paragraph2"
          semibold
          color="pebble"
          className="preview-design-editor-nav__title"
        >
          Edit Invitation design
        </Typography>
      )}
      <Button
        variant="primary"
        data-qa-id="save-design-changes"
        size={isDesktop ? 'small' : 'large'}
        onClick={handleSave}
      >
        Save {isDesktop ? 'changes' : ''}
      </Button>
      <ResponsiveModal
        isOpen={leaveModalOpen}
        onClose={closeLeaveModal}
        primaryText="Exit anyway"
        primaryAction={confirmCancel}
        secondaryText="Keep editing"
        title="Want to exit?"
        body="Your edits have not been saved."
      />
    </div>
  );
};

interface PreviewDesignEditorNavProps {
  containerId?: string;
}

export const PreviewDesignEditorNav: FC<PreviewDesignEditorNavProps> = memo(({containerId}) => {
  const [savedDesign] = useState(useSelector(selectFabricDesign));

  if (containerId) {
    return createPortal(
      <PreviewDesignEditorNavContent savedDesign={savedDesign} />,
      document.getElementById(containerId) ?? document.body
    );
  }

  return <PreviewDesignEditorNavContent savedDesign={savedDesign} />;
});
